import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Card,
  CardBody,
  Col,
  Form,
  Input,
  Label,
  Row,
  FormFeedback,
} from "reactstrap";
import { Btn } from "../../../AbstractElements";
import Breadcrumbs from "../../../CommonElements/Breadcrumbs";
import axios, { AxiosError } from "axios";
import { baseUrl, uploadFile } from "../../../Utils/FileUpload";
import {
  Submit,
  AddPackagePaymentSettingTitle,
  PackagePaymentSettingTitle,
  AddVideoPackagePaymentSettingTitle,
} from "../../../Utils/Constants";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import useCustomToasts from "../../../hooks/useCustomToast";

interface FormValues {
  base_amount: number;
  base_customer_credit_point: number;
  base_video_call_duration: number;
  base_agent_point: number;
  base_agent_payment: number;
}

interface ErrorResponse {
  message: string;
}

export default function AddVideoPackagePaymentSetting() {
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FormValues>();
  const navigate = useNavigate();
  const { SuccessToast, ErrorToast } = useCustomToasts(navigate);
  const [isPasswordVisible, setPasswordVisible] = useState(false);

  const onSubmit = async (data: FormValues) => {
    setIsButtonDisabled(true);
    console.log(data, "data");
    const baseAmount = Number(data.base_amount);
    const agentPayment = Number(data.base_agent_payment);

    if (agentPayment >= baseAmount) {
      ErrorToast(
        "Agent payment cannot be greater than or equal to base amount"
      );
      setIsButtonDisabled(false);
      return;
    }
    try {
      const payload = {
        base_amount: data.base_amount,
        base_customer_credit_point: data.base_customer_credit_point,
        base_video_call_duration: data.base_video_call_duration,
        base_agent_point: data.base_agent_point,
        base_agent_payment: data.base_agent_payment,
      };
      // console.log(payload,"payload");
      const token = localStorage.getItem("token");

      const response = await axios.post(
        `${baseUrl}video_package_payment_setting/store`,
        payload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200 || response.status === 201) {
        SuccessToast(
          "Data added successfully",
          "/view_video_package_payment_setting"
        );
      } else {
        ErrorToast("Failed to add");
      }
    } catch (err) {
      const axiosError = err as AxiosError;
      const errorMessage =
        (axiosError.response?.data as ErrorResponse)?.message ||
        "An unexpected error occurred";

      console.error("Error:", errorMessage);
      ErrorToast(errorMessage);
    } finally {
      setTimeout(() => {
        setIsButtonDisabled(false);
      }, 2000);
    }
  };

  // Prevent form submission on Enter key press
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
    }
  };

  return (
    <>
      <Breadcrumbs
        pageTitle={AddVideoPackagePaymentSettingTitle}
        parent={PackagePaymentSettingTitle}
        title={AddVideoPackagePaymentSettingTitle}
      />
      <Col xl={12} className="mb-lg-3">
        <Card>
          <CardBody className="custom-input form-validation">
            <Form onSubmit={handleSubmit(onSubmit)}>
              <Row className="g-3">
                <Col xs={12}>
                  <Label htmlFor="base_amount">Recharge Amount </Label>
                  <Controller
                    name="base_amount"
                    control={control}
                    rules={{
                      required: "Base amount is required",
                      min: { value: 0, message: "Must be positive" },
                    }}
                    render={({ field }) => (
                      <>
                        <Input
                          type="number"
                          step="0.01"
                          id="base_amount"
                          placeholder="Base Amount"
                          {...field}
                          style={{
                            color: "var(--input-text-color)",
                            borderColor: errors.base_amount ? "red" : "",
                          }}
                          invalid={!!errors.base_amount}
                          onKeyDown={handleKeyDown}
                        />
                        {errors.base_amount && (
                          <FormFeedback>
                            {errors.base_amount.message}
                          </FormFeedback>
                        )}
                      </>
                    )}
                  />
                </Col>

                <Col xs={12}>
                  <Label htmlFor="base_customer_credit_point">
                    Customer Points
                  </Label>
                  <Controller
                    name="base_customer_credit_point"
                    control={control}
                    rules={{
                      required: "Credit point is required",
                      min: { value: 0, message: "Must be positive" },
                    }}
                    render={({ field }) => (
                      <>
                        <Input
                          type="number"
                          step="0.01"
                          id="base_customer_credit_point"
                          placeholder="Customer Credit Point"
                          {...field}
                          style={{
                            color: "var(--input-text-color)",
                            borderColor: errors.base_customer_credit_point
                              ? "red"
                              : "",
                          }}
                          invalid={!!errors.base_customer_credit_point}
                          onKeyDown={handleKeyDown}
                        />
                        {errors.base_customer_credit_point && (
                          <FormFeedback>
                            {errors.base_customer_credit_point.message}
                          </FormFeedback>
                        )}
                      </>
                    )}
                  />
                </Col>

                <Col xs={12}>
                  <Label htmlFor="base_video_call_duration">
                    Video Call Duration (mins)
                  </Label>
                  <Controller
                    name="base_video_call_duration"
                    control={control}
                    rules={{
                      required: "Call duration is required",
                      min: { value: 0, message: "Must be positive" },
                    }}
                    render={({ field }) => (
                      <>
                        <Input
                          type="number"
                          step="0.01"
                          id="base_video_call_duration"
                          placeholder="Call Duration"
                          {...field}
                          style={{
                            color: "var(--input-text-color)",
                            borderColor: errors.base_video_call_duration
                              ? "red"
                              : "",
                          }}
                          invalid={!!errors.base_video_call_duration}
                          onKeyDown={handleKeyDown}
                        />
                        {errors.base_video_call_duration && (
                          <FormFeedback>
                            {errors.base_video_call_duration.message}
                          </FormFeedback>
                        )}
                      </>
                    )}
                  />
                </Col>

                <Col xs={12}>
                  <Label htmlFor="base_agent_point">Agent Points</Label>
                  <Controller
                    name="base_agent_point"
                    control={control}
                    rules={{
                      required: "Agent point is required",
                      min: { value: 0, message: "Must be positive" },
                    }}
                    render={({ field }) => (
                      <>
                        <Input
                          type="number"
                          step="0.01"
                          id="base_agent_point"
                          placeholder="Agent Point"
                          {...field}
                          style={{
                            color: "var(--input-text-color)",
                            borderColor: errors.base_agent_point ? "red" : "",
                          }}
                          invalid={!!errors.base_agent_point}
                          onKeyDown={handleKeyDown}
                        />
                        {errors.base_agent_point && (
                          <FormFeedback>
                            {errors.base_agent_point.message}
                          </FormFeedback>
                        )}
                      </>
                    )}
                  />
                </Col>

                <Col xs={12}>
                  <Label htmlFor="base_agent_payment">Agent Payment</Label>
                  <Controller
                    name="base_agent_payment"
                    control={control}
                    rules={{
                      required: "Agent payment is required",
                      min: { value: 0, message: "Must be positive" },
                    }}
                    render={({ field }) => (
                      <>
                        <Input
                          type="number"
                          step="0.01"
                          id="base_agent_payment"
                          placeholder="Agent Payment"
                          {...field}
                          style={{
                            color: "var(--input-text-color)",
                            borderColor: errors.base_agent_payment ? "red" : "",
                          }}
                          invalid={!!errors.base_agent_payment}
                          onKeyDown={handleKeyDown}
                        />
                        {errors.base_agent_payment && (
                          <FormFeedback>
                            {errors.base_agent_payment.message}
                          </FormFeedback>
                        )}
                      </>
                    )}
                  />
                </Col>

                <Col xs={12} className="mt-2">
                  <Btn color="primary" disabled={isButtonDisabled}>
                    {Submit}
                  </Btn>
                </Col>
              </Row>
            </Form>
          </CardBody>
        </Card>
      </Col>
      <ToastContainer
        toastStyle={{
          userSelect: "none",
        }}
      />
    </>
  );
}
